import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatReportDate } from "@/utils/tlRecordingReportHelpers";
import {
  buildAgentNameLookup,
  buildKaliteliImportPreview,
  filterSheetRowsWithAgentMatch,
} from "@/utils/kaliteliDataExcelImport";

const KaliteliDataImportModal = ({
  open,
  onClose,
  fileName,
  sheets,
  agents,
  isDark,
  textClass,
  labelClass,
  importing,
  onConfirm,
  importResult,
}) => {
  const [step, setStep] = useState("preview");
  const [selectedKeys, setSelectedKeys] = useState({});

  const preview = useMemo(
    () => (sheets?.length ? buildKaliteliImportPreview(sheets, agents) : null),
    [sheets, agents],
  );

  const lookup = useMemo(() => buildAgentNameLookup(agents), [agents]);

  useEffect(() => {
    if (!open) return;
    setStep(importResult ? "done" : "preview");
    if (!preview) return;
    const sel = {};
    preview.sheetPreviews.forEach((s) => { sel[s.key] = true; });
    setSelectedKeys(sel);
  }, [open, preview, importResult]);

  const selectedStats = useMemo(() => {
    if (!preview) return { sheets: 0, rows: 0, matchedRows: 0 };
    let rows = 0;
    let matchedRows = 0;
    const count = preview.sheetPreviews.filter((s) => selectedKeys[s.key]).length;
    preview.sheetPreviews.forEach((s) => {
      if (!selectedKeys[s.key]) return;
      rows += s.row_count;
      matchedRows += s.matched_row_count;
    });
    return { sheets: count, rows, matchedRows };
  }, [preview, selectedKeys]);

  const handleConfirm = useCallback(() => {
    if (!preview) return;
    const selectedSheets = preview.sheetPreviews
      .filter((s) => selectedKeys[s.key])
      .map((s) => filterSheetRowsWithAgentMatch(s.raw, lookup));
    onConfirm({
      sheets: selectedSheets,
      agent_name_map: {},
      only_matched_rows: true,
    });
  }, [preview, selectedKeys, lookup, onConfirm]);

  const panelClass = isDark ? "bg-slate-900 border-slate-700 text-white" : "";
  const blockClass = isDark ? "border-slate-700 bg-slate-950/50" : "border-gray-200 bg-gray-50/80";
  const canImport = selectedStats.sheets > 0 && selectedStats.matchedRows > 0;

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !importing && onClose()}>
      <DialogContent className={`max-w-2xl max-h-[90vh] overflow-hidden flex flex-col ${panelClass}`}>
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2 pr-6">
            <FileSpreadsheet className={`w-5 h-5 shrink-0 ${isDark ? "text-teal-400" : "text-teal-600"}`} />
            Kaliteli Data Excel Import
          </DialogTitle>
          {fileName && (
            <p className={`text-xs font-normal ${labelClass}`}>{fileName}</p>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-1 pr-1">
          {step === "done" && importResult && (
            <div className={`rounded-lg border p-4 ${blockClass}`}>
              <div className="flex items-start gap-3">
                <CheckCircle2 className={`w-8 h-8 shrink-0 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                <div>
                  <p className={`text-sm font-semibold ${textClass}`}>Import tamamlandı</p>
                  <ul className={`text-xs mt-2 space-y-1 ${labelClass}`}>
                    <li>{importResult.batches ?? 0} sheet işlendi</li>
                    {(importResult.inserted ?? 0) > 0 && (
                      <li>{importResult.inserted} yeni satır eklendi</li>
                    )}
                    {(importResult.updated ?? 0) > 0 && (
                      <li>{importResult.updated} satır güncellendi</li>
                    )}
                    {(importResult.unchanged ?? 0) > 0 && (
                      <li>{importResult.unchanged} satır zaten aynıydı (tekrar eklenmedi)</li>
                    )}
                    {(importResult.deleted ?? 0) > 0 && (
                      <li>{importResult.deleted} eski satır kaldırıldı (Excel&apos;de yoktu)</li>
                    )}
                    {(importResult.rows ?? 0) === 0 && (importResult.unchanged ?? 0) > 0 && (
                      <li className={isDark ? "text-emerald-300" : "text-emerald-800"}>Veri değişmedi — yinelenen kayıt oluşturulmadı.</li>
                    )}
                    {(importResult.skipped ?? 0) > 0 && (
                      <li>{importResult.skipped} satır atlandı (isim eşleşmedi)</li>
                    )}
                    {(importResult.unmatched?.length ?? 0) > 0 && (
                      <li className={isDark ? "text-amber-300" : "text-amber-800"}>
                        Eşleşmeyen isimler: {[...new Set(importResult.unmatched)].join(", ")}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {step === "preview" && preview && (
            <>
              {preview.agentPoolSize === 0 && (
                <div className={`rounded-lg border px-3 py-2 text-xs ${isDark ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
                  Panelde agent listesi yüklenemedi veya firma filtresi boş. Sayfayı yenileyin veya (süper admin) firma seçin.
                </div>
              )}

              <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs ${labelClass}`}>
                {[
                  { label: "Sheet", value: preview.sheetPreviews.length },
                  { label: "Excel satır", value: preview.totalRows },
                  { label: "Eşleşen satır", value: preview.totalMatchedRows },
                  { label: "Panel agent", value: preview.agentPoolSize },
                ].map((item) => (
                  <div key={item.label} className={`rounded-lg border px-2 py-2 ${blockClass}`}>
                    <div className={`text-lg font-semibold ${textClass}`}>{item.value}</div>
                    <div>{item.label}</div>
                  </div>
                ))}
              </div>

              <p className={`text-[11px] ${labelClass}`}>
                Eşleştirme <strong className="font-medium">ad soyad</strong> ile yapılır (büyük/küçük harf, Türkçe karakter, ad-soyad sırası toleranslı).
                Yalnızca eşleşen satırlar kaydedilir; eşleşmeyenler atlanır.
              </p>

              <div>
                <p className={`text-xs font-semibold mb-2 ${textClass}`}>Sheet seçimi</p>
                <div className="space-y-2">
                  {preview.sheetPreviews.map((sheet) => (
                    <label
                      key={sheet.key}
                      className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${blockClass}`}
                    >
                      <Checkbox
                        checked={!!selectedKeys[sheet.key]}
                        onCheckedChange={(checked) => {
                          setSelectedKeys((prev) => ({ ...prev, [sheet.key]: !!checked }));
                        }}
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium ${textClass}`}>{sheet.sheet_title}</p>
                        <p className={`text-[11px] ${labelClass}`}>
                          {formatReportDate(sheet.period_start)} – {formatReportDate(sheet.period_end)}
                          {" · "}
                          {sheet.matched_row_count}/{sheet.row_count} satır eşleşti
                        </p>
                        {sheet.unmatched_names.length > 0 && (
                          <p className={`text-[10px] mt-1 ${isDark ? "text-amber-300/90" : "text-amber-700"}`}>
                            Atlanacak isimler: {sheet.unmatched_names.join(", ")}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {preview.allUnmatched.length > 0 && (
                <div className={`rounded-lg border p-3 ${blockClass}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className={`w-4 h-4 shrink-0 ${isDark ? "text-amber-400" : "text-amber-600"}`} />
                    <p className={`text-xs font-semibold ${textClass}`}>
                      {preview.allUnmatched.length} isim otomatik eşleşmedi (yüklemede atlanır)
                    </p>
                  </div>
                  <p className={`text-[11px] leading-relaxed ${labelClass}`}>
                    {preview.allUnmatched.join(", ")}
                  </p>
                </div>
              )}

              {preview.totalMatchedRows > 0 && (
                <p className={`text-xs flex items-center gap-2 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                  <CheckCircle2 className="w-4 h-4" />
                  {preview.totalMatchedRows} satır agent ad soyad ile eşleşti ve içe aktarılabilir.
                </p>
              )}
              <p className={`text-xs ${labelClass}`}>
                Aynı Excel&apos;i tekrar yüklerseniz çift kayıt oluşmaz; aynı satırlar güncellenir veya atlanır.
              </p>
            </>
          )}

          {!preview && step === "preview" && (
            <p className={`text-sm text-center py-8 ${labelClass}`}>Dosya okunamadı veya uygun sheet yok.</p>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:gap-2">
          {step === "done" ? (
            <Button type="button" onClick={onClose}>Kapat</Button>
          ) : (
            <>
              <Button type="button" variant="outline" disabled={importing} onClick={onClose}>
                İptal
              </Button>
              <Button
                type="button"
                disabled={!canImport || importing || !preview}
                className="bg-teal-600 hover:bg-teal-500"
                onClick={handleConfirm}
                title={!canImport ? "Eşleşen satır yok" : undefined}
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {selectedStats.matchedRows > 0
                  ? `${selectedStats.matchedRows} eşleşen satırı yükle`
                  : "Eşleşen satır yok"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KaliteliDataImportModal;
